import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import type {
  ABTestLifecycleState,
  CohortNames,
  IAbTestingState,
} from '@bridebook/toolbox/src/ab-testing/lib/types';

export interface AbTestingState {
  abTesting: IAbTestingState;
}

/**
 * Select the abTests slice from the state.
 */
export const getAbTests = (state: AbTestingState): Record<string, CohortNames> =>
  state.abTesting.abTests;

/**
 * Select the abTestsLoaded flag from the state.
 */
const getAbTestsLoaded = (state: AbTestingState): boolean => state.abTesting.abTestsLoaded;

/**
 * Returns test variant if activated or falls back to 'control'
 *
 * @deprecated use getAbTestLifecycleState instead
 */
export const getTestVariant = createCachedSelector(
  getAbTests,
  (_: unknown, id: string) => id,
  (abTests, id): CohortNames => abTests?.[id] || 'control',
)((_, id) => id);

/**
 * Returns true if the test has a variant in store
 *
 * @deprecated use getAbTestsLifecycleState instead
 */
export const getIsTestActivated = createCachedSelector(
  getAbTests,
  (_: AbTestingState, id: string) => id,
  (abTests, id): boolean => !!abTests && Object.keys(abTests).includes(id),
)((_, id) => id);

/**
 * Returns whether the AB tests are loaded
 *
 * @deprecated use getAbTestsLifecycleState instead
 */
export const getAreTestsLoaded = createSelector(
  getAbTestsLoaded,
  (abTestsLoaded): boolean => abTestsLoaded,
);

/**
 * Select the abTestsDetailedStatus map from the state.
 */
const getAbTestsLifecycleState = (state: AbTestingState): Record<string, ABTestLifecycleState> =>
  state.abTesting.abTestsLifecycleState;

/**
 * Returns the detailed state for a specific AB test (status + variant).
 * Returns undefined if the test has not been initialized yet.
 */
export const getAbTestLifecycleState = createCachedSelector(
  getAbTestsLifecycleState,
  (_: AbTestingState, id: string) => id,
  (detailedStatus, id): ABTestLifecycleState | undefined => detailedStatus?.[id],
)((_, id) => id);
